import React, { useContext, useEffect, useState } from 'react';
import AppSnackbar from '../../common/AppSnackbar';
import { State, StateEvent } from '@sierralabs/workflow';
import { UserContext } from '../../user/User.context';
import { Task, TaskEntity } from './Task.interface';
import { TaskContext, useGetWorkflowMachineState } from './Task.machine';
import { useUpdateTask } from './Task.hooks';
import { Dialog, DialogTitle, CircularProgress, Box } from '@material-ui/core';

export interface TaskBoardStatusActionProps {
  selectedTask: Task;
  taskEntity: TaskEntity | undefined;
  event: any;
  doTransition: (value: boolean) => void;
  onChange: (task: Task) => void;
}

export const TaskBoardStatusAction: React.FC<TaskBoardStatusActionProps> = ({
  selectedTask,
  taskEntity,
  event,
  doTransition,
  onChange
}) => {
  const { user } = useContext(UserContext);
  const [task, setTask] = useState(selectedTask);
  const { machine, stateNode, render, workflow } =
    useGetWorkflowMachineState(task);
  const { updatedTask, isUpdating, updateError, updateTask } = useUpdateTask(
    selectedTask.id || 0
  );

  useEffect(() => {
    setTask(selectedTask);
  }, [selectedTask]);

  useEffect(() => {
    if (updatedTask) {
      onChange(updatedTask);
      setTask(updatedTask);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (event && stateNode) {
      checkEvent(stateNode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, taskEntity]);

  const checkEvent = (stateNode: any) => {
    const events = stateNode.events as any;
    const eventFound = events.find((e: StateEvent) => e.name === event.name);
    if (!eventFound) {
      doTransition?.(false);
      return;
    }
    if (canTransition(eventFound)) {
      handleTransition(eventFound);
    } else {
      doTransition?.(false);
    }
  };

  const handleTransition = (event: StateEvent) => {
    const state: State<TaskContext> = {
      path: stateNode!.path,
      context: { event, task, taskEntity, user: user!, workflow: workflow! }
    };
    machine
      ?.transition(state, event.name)
      .then(newState => {
        if (newState.path === state.path) {
          cancelTransition(state);
          return;
        }
        task.status = newState.path;
        updateTask({
          status: newState.path,
          event: event.toJson(),
          reason: newState.context.reason,
          subTaskUserIds: newState.context.subTaskUserIds,
          pluginData: newState.context.pluginData
        });
      })
      .catch(error => {
        console.error(error);
        cancelTransition(state);
      });
  };

  const cancelTransition = (state: { path: string }) => {
    task.status = state.path;
    onChange(task);
    doTransition?.(false);
  };

  const canTransition = (event: StateEvent) => {
    const state: State<TaskContext> = {
      path: stateNode!.path,
      context: { event, task, taskEntity, user: user!, workflow: workflow! }
    };
    return machine?.canTransition(state, event.name);
  };

  return (
    <>
      {render}
      {isUpdating && (
        <AppSnackbar severity='info' open={isUpdating}>
          Updating...
        </AppSnackbar>
      )}
      <AppSnackbar severity='error' open={Boolean(updateError)}>
        There was an error updating the status.
      </AppSnackbar>
    </>
  );
};
